<script lang="ts">
  import { readRefusedRows } from "./refusedRows";
  /**
   * THE COCKPIT'S PRIMARY INTERACTIVE CHART (R765③, owner mandate 17:2xZ).
   *
   * Price, upgraded CVD line/delta, and V-split share one four-pane
   * Lightweight Charts instance,
   * so each column owns one time scale, crosshair, zoom, and pan state.
   *
   * ⛔ WHY THIS FILE EXISTS AT ALL, STATED PLAINLY: every part of it already shipped. R504
   * stamped the slice "heads-up only — the cockpit column is untouched" as a SAFETY property,
   * and that stamp silently became a PERMANENT STATE. R765① names it as the day's class —
   * ***BUILT, KEYED, LANDED, NEVER CONNECTED.*** The owner lifted the bar by word.
   *
   * ⛔ `PrimaryChartPanel` remains the sole fetch owner. `SessionCvdPane` consumes its admitted
   * candles and CVD for all four panes without creating a second route owner.
   */
  import { onMount, tick } from "svelte";
  import SessionCvdPane from "./lwc/SessionCvdPane.svelte";
  import { loadZeroDte } from "./zeroDteLoad";
  import { fetchDealerLevels, fetchWallAlerts, formatAge, setServedVenueState, startVwapPolling, type Candle, type DealerLevelsResponse, type VenueState, type VwapLevel, type VwapLevelsResponse, type WallAlertsResponse, type WallAlertEvent } from "./api";
  import { type ResidueEntry, wallDisplayState } from "./order33State.ts";
  import {
    basisRatio, chartLevelSet, chartLevelsFromDealer, levelSetGlyphNotes, levelSetPillNote, rememberLevelSet, sameDoorWallVerdict, storedLevelSet,
    LEVEL_SET_NAMES, type LevelSetBody, type LevelSetName,
  } from "./dealer";
  import { admitCvd, type Cvd2Bar, type Cvd2Feed, type DrawableCvd, type PriceBar } from "./cvd2";
  import { mayClaimServed } from "./lwc/paneGaps";
  import { leg } from "./feedLeg";
  import RungPills from "./RungPills.svelte";
  import StalenessChip from "./StalenessChip.svelte";
  import AgePill from "./AgePill.svelte";
  import { keptPayloadAfterFailure } from "./dataAge";
  import VenueClosedBanner from "./VenueClosedBanner.svelte";
  import { formingBar, type FormingCandle } from "./formingBar";
  import { writeLiveMeasurement } from "./liveMeasurement";
  import { pollIntervalMs, pollDue } from "./tickPoll";
  import { RANGE_CADENCE_MS } from "./rangeCadence.ts";
  import { feedState } from "./session";
  import {
    RUNG_LADDER,
    rungFromKey,
    rungKey,
    chartTitleRung,
    rungLabel,
    rungOptions,
    rungSource,
    rungSpanMs,
    tfRung,
    type Rung,
    type RungKey,
  } from "./rungLadder.ts";
  import { admitSubmin, readRungPayload, rungUrl } from "./rungFetch.ts";
  import {
    alignedCandleHistoryUrl,
    candleRungUrl,
    canPageAlignedCandles,
    canPageDeepCandles,
    mergeCandlePages,
    candlePagePriceError,
    preferredClosedVenueState,
    readCandlePage,
    readVwapCoveragePage,
    shouldLoadOlderCandles,
    vwapCoverageTargetTo,
    vwapCurveCoverageUrls,
  } from "./candlePaging.ts";
  import {
    CVD_PAGE_LIMIT,
    canPageDeepCvd,
    cvdPageUrl,
    mergeCvdPages,
    readCvdPage,
    withCvdHistory,
  } from "./cvdPaging.ts";
  import {
    eagerHistoryPageBudget,
    needsEagerHistory,
    runSequentialHistoryFill,
    shouldFetchForEmptyRange,
    type HistoryPageResult,
  } from "./eagerHistory.ts";
  import {
    captureVisibleLogicalAnchor,
    chartTimeAtClientX,
    clearChartTimeCursor,
    onChartUserCrosshair,
    observePositiveChartHost,
    restoreVisibleLogicalAnchor,
    setChartTimeCursor,
    syncVwapCurveSeries,
  } from "./lwcChartRegistry.ts";
  import { candleForMinute, sharedTideCursor } from "./tide.ts";
  import { attachWithRetry, VWAP_SYNC_MAX_RETRIES } from "./vwapAttachRetry.ts";
  import { attachWhenHostReady, VWAP_HOST_READY_TIMEOUT_MS } from "./vwapAttachRetry.ts";
  import { resolveTokens, watchTheme } from "../spike/lwcTokens.ts";
  import { observeMountedPaneCount } from "./paneCensus.ts";
  import type { FocusChartData } from "./focusChartData.ts";
  import type { CvdDisplayMode } from "./lwc/linkedStudyModel.ts";
  import {
    anchorBarAtTime,
    buildVwapCurves,
    CALENDAR_VWAP_CURVE_IDS,
    calendarVwapAnchor,
    cmeSessionAnchor,
    PULLED_AVWAP_CURVE_PREFIX,
    rememberVwapCurvePrefs,
    storedVwapCurvePrefs,
    vwapCurveToken,
    vwapLevelToggle,
    vwapLevelVisible,
    type VwapCurvePrefs,
    type VwapCurveToken,
    type VwapCurveToggle,
  } from "./vwapCurves.ts";

  interface RangeFeed {
    ok: boolean;
    root?: string;
    ticks?: number;
    bars?: Array<{
      t0: number; t1: number; minutes: number; o: number; h: number; l: number; c: number;
      contracts: number; max_bar: number; side: string; effort: number | null;
    }>;
    age_s?: number | null;
    budget_s?: number | null;
    stale?: boolean | null;
    state?: string;
    reason?: string;
  }

  /** Re-read completed native bars so late vendor corrections replace cached VWAP inputs. */
  const VWAP_REFRESH_OVERLAP_MS = 15 * 60_000;

  interface Props {
    /** Stable application identity. Never derive storage or series ownership from visible copy. */
    chartId: "primaryA" | "primaryB";
    /**
     * ⛔ WHICH OF THE TWO CHARTS THIS IS — R771① puts two instances side by side, and
     * `check:mounts` requires each declared panel to own a marker that is UNIQUE and can go
     * ABSENT with its panel. Two instances of one component emit one string, so the panel map
     * cannot tell them apart.
     * ⚠ AND THE RUNG CANNOT BE THAT MARKER: R772② makes the timeframe user-selectable per chart,
     * so "ES1! · 12m · …" stops identifying panel B the moment the owner clicks 3h.
     * ⇒ *** A MARKER PINNED TO MUTABLE STATE IS A MARKER THAT DIES WHEN THE USER TOUCHES IT. ***
     * So the HOST names its slot — the same rule this panel already applies to SessionCvdPane,
     * one level up.
     */
    slot?: string;
    /** Immutable marker emitted by the linked four-pane chart instance itself. */
    stackMarker?: string;
    api: string;
    /** The rung the primary chart draws. The rung panes below keep their own. */
    rung?: RungKey;
    limit?: number;
    height?: number;
    /** Focus cell height. When present, the panel and linked chart flex to this fitted track. */
    focusHeight?: number | null;
    /** Shared `/api/es/ticks` sample. This panel never creates a second tick poll. */
    tickPrice?: number | null;
    /** Vendor timestamp of `tickPrice`, epoch ms. */
    tickMs?: number | null;
    /** Wall-clock-aware freshness verdict owned by the shared App mount. */
    tickFresh?: boolean;
    chartRole?: "lower" | "higher";
    /** Focus layout delegates the range series to its single dedicated range tier. */
    rangeEnabled?: boolean;
    activeWindow?: { from: number; to: number } | null;
    onVisibleWindowChange?: ((window: { from: number; to: number } | null) => void) | null;
    /** Read-only reuse port for focus tiers. It never owns or starts a fetch. */
    onFocusData?: ((data: FocusChartData) => void) | null;
    cvdMode?: CvdDisplayMode | null;
    onCvdModeChange?: ((mode: CvdDisplayMode) => void) | null;
  }
  let {
    api,
    chartId,
    rung = $bindable<RungKey>("1"),
    limit = 300,
    height = 460,
    focusHeight = null,
    slot = "",
    stackMarker = "ES LINKED STUDIES",
    tickPrice = null,
    tickMs = null,
    tickFresh = false,
    chartRole = "lower",
    rangeEnabled = true,
    activeWindow = null,
    onVisibleWindowChange = null,
    onFocusData = null,
    cvdMode = null,
    onCvdModeChange = null,
  }: Props = $props();

  let cvd = $state<DrawableCvd | null>(null);
  let cvdFeed = $state<Cvd2Feed | null>(null);
  let rangeFeed = $state<RangeFeed | null>(null);
  let rangeTicks = $state<8 | 12>(8);
  let rangeGen = 0;
  let price = $state<PriceBar[]>([]);
  let candles = $state<Candle[]>([]);
  let declaredResidue = $state<readonly ResidueEntry[]>([]);
  function mergeDeclaredResidue(existing: readonly ResidueEntry[], incoming: readonly ResidueEntry[]): readonly ResidueEntry[] {
    const byMinute = new Map([...existing, ...incoming].map((entry) => [Date.parse(entry.minute), entry]));
    return [...byMinute.values()].sort((a, b) => Date.parse(a.minute) - Date.parse(b.minute));
  }

  let forming = $state<FormingCandle | null>(null);
  let formingBarMs: number | null = null;
  let formingTickKey = "";
  let candleRoot = $state<string | null>(null);
  let candleServedRung = $state<string | null>(null);
  let candleVenueState = $state<VenueState | null>(null);
  let candleVenueStateKnown = $state(false);
  let loadedCandleRung = $state<string | null>(null);
  let candleNextBefore = $state<number | null>(null);
  let candleHasMore = $state(false);
  /**
   * ORDER 44 — the CVD half of the same prepend, held SEPARATELY from the live feed.
   *
   * ⛔ IT CANNOT LIVE INSIDE `cvdFeed`. `load()` replaces that object every poll with the
   * newest ~300 bars, so history merged into it would be silently discarded five seconds after
   * it was fetched — the pan would work once, look like it worked, and then quietly undo
   * itself. Keeping the accumulated pages in their own array and re-merging AT COMMIT is what
   * makes the depth survive the live poll it shares a panel with.
   */
  let cvdOlderBars = $state<Cvd2Bar[]>([]);
  let cvdNextBefore = $state<number | null>(null);
  let cvdHasMore = $state(false);
  let historyDepth = $state(0);
  let historyLoading = $state(false);
  let historyError = $state<string | null>(null);
  let historyEpoch = 0;
  let historyGestureArmed = false;
  let historyGestureAt = 0;
  let primarySection = $state<HTMLElement | null>(null);
  let tideCursorMinute = $state<number | null>(null);
  let stopChartUserCrosshair: (() => void) | null = null;
  let chartCursorHost: HTMLElement | null = null;
  /** Price-only history held for exact curve math; it never widens the aligned price/CVD panes. */
  let curveCandles = $state<Candle[]>([]);
  let curveCoverageFrom = $state<number | null>(null);
  let curveCoverageTo = $state<number | null>(null);
  let pulledAnchors = $state<number[]>([]);
  let curvePrefs = $state<VwapCurvePrefs>({ session: false, avwap: false, D: false, W: false, M: false });
  let curveColors = $state<Record<VwapCurveToken, string>>({
    "--vwap": "", "--magnet": "", "--up": "", "--flip": "",
  });
  let curvePrefsError = $state<string | null>(null);
  let curveCoverageError = $state<string | null>(null);
  let curveCoverageFlight: Promise<void> | null = null;
  let curveCoveragePending = false;
  let anchorGesture: { pointerId: number; x: number; y: number } | null = null;
  let infoOpen = $state(false);
  let chartInfoKey = $derived((slot || "chart").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
  let chartInfoId = $derived(`${chartInfoKey}-chart-info`);
  let studyInfoId = $derived(`${chartInfoKey}-study-info`);

  const linkedPaneToken = "LINKED-PANE";
  let linkedPaneMarker = $state(linkedPaneToken);

  /**
   * R814 — THE OWNER'S WORDS: "we're not seeing the call wall, put wall, gamma flips on the
   * TradingView charts." They were never absent from the app: `LwcPriceCanvas` has drawn them since
   * 08-09 and the ES1! panes show them. **This chart renders through `SessionCvdPane`, which was
   * never given the overlay.** ⇒ ***TWO RENDERERS, ONE OF THEM WIRED — the feature existed and the
   * wire did not.***
   *
   * ⛔ THE SAME LEG AS THE RAIL, NOT A SECOND ONE: `loadZeroDte(fetchDealerLevels)`, whose refusal
   * lives inside the loader so a transport failure can never be substituted with another source.
   */
  let levels0dte = $state<DealerLevelsResponse | null>(null);
  let levelsFetchedAtMs = $state<number | null>(null);
  let levelsRequestKey = $state<string | null>(null);
  let levels0dteError = $state<string | null>(null);
  let vwapFeed = $state<VwapLevelsResponse | null>(null);
  let vwapError = $state<string | null>(null);
  let vwapLoaded = $state(false);
  let wallFeed = $state<WallAlertsResponse | null>(null);
  let wallFetchedAtMs = $state<number | null>(null);
  let wallRequestKey = $state<string | null>(null);
  const wallDoorKey = "/api/es/wall-alerts?limit=50";
  let wallError = $state<string | null>(null);
  let wallTimer: ReturnType<typeof setInterval> | null = null;
  let wallState = $derived(wallFeed?.meta?.state ?? wallFeed?.state ?? null);
  let wallRefusalCount = $derived(wallFeed?.meta?.refusals?.length ?? 0);
  // Chains 116 + 117 merged at the desk (R1926): the CLOSED verdict is read from the candles envelope first
  // (117, preferredClosedVenueState), and the awaiting-basis chip can never be ink beside the CLOSED banner (116).
  let cashVenueClosed = $derived(preferredClosedVenueState(
    candleVenueStateKnown,
    candleVenueState,
    levels0dte?.venue_state,
    wallFeed?.venue_state,
  ) as VenueState | null);
  let wallAwaitingBasis = $derived(cashVenueClosed === null && wallState === "NO_SERVABLE_ALERTS");
  let wallAgeVerdict = $derived(sameDoorWallVerdict(wallFeed));

  /**
   * ⛔ THE BASIS IS MEASURED, NEVER ASSUMED. SPX levels on an ES axis are ~15 points out and look
   * entirely plausible — a wrong line prompts no investigation, which is why the tie-break law
   * exists. `basisRatio` takes the SPX spot the levels payload itself carries and THIS chart's own
   * last close, so the mapping belongs to the chart it is drawn on.
   * ⚠ `null` ratio ⇒ NO LEVELS. Drawing unmapped SPX numbers would be the exact defect.
   */
  let esLast = $derived(candles.length ? candles[candles.length - 1].c : null);
  let levelRatio = $derived(basisRatio(levels0dte?.spot ?? null, esLast));
  /**
   * HUB-LEVEL-SET-1 L2: ONE level set per chart, chosen by the LEVEL SET pill and persisted per chart.
   * The 0DTE set is today's walls with the vendor's vol flip (D3 (a)); the UW vol set is the vendor's
   * four levels. Both reach ES through the same measured ratio (L3).
   */
  let levelSet = $state<LevelSetName>("0DTE");
  let levelSetError = $state<string | null>(null);
  let levelSetBody = $derived(levels0dte as (DealerLevelsResponse & LevelSetBody) | null);
  let levelSetView = $derived(
    levelSetBody?.ok && levelRatio !== null ? chartLevelSet(levelSet, levelSetBody, levelRatio) : { lines: [], absent: [] },
  );
  let chartLevels = $derived(levelSetView.lines);
  let levelScopeToken = $derived(levelSet === "0DTE" ? "0DTE ES" : "ALL SPX");
  /** The active pill's note: an unserved read, the stale age and a preference failure, all at once (levelSetPillNote). */
  let levelSetNote = $derived(levelSetPillNote(levelSet, levelSetBody?.ok ? levelSetBody : null, formatAge, levelSetError));
  /** L3 + L4: values, both prices, source and as-of, and R410's crossing live behind the glyph only. */
  let levelSetNotes = $derived(
    levelSetBody?.ok && levelRatio !== null ? levelSetGlyphNotes(levelSet, levelSetBody, levelRatio) : [],
  );
  function chooseLevelSet(set: LevelSetName) {
    levelSet = set;
    levelSetError = rememberLevelSet(chartId, set);
  }
  /** ⛔ PER-LEG, NOT ONE FLAG. A single error cannot say WHICH feed died, and the healthy leg
      is still worth drawing — so long as the dead one is NAMED. */
  let cvdErr = $state<string | null>(null);
  let priceErr = $state<string | null>(null);
  let err = $state<string | null>(null);
  let loaded = $state(false);
  /** Advances only when the paired price/CVD request commits; history paints are not polls. */
  let dataPollSeq = $state(0);
  /** The freshness register commits with the bars; a new header may not inherit an old rung's age. */
  let ageS = $state<number | null>(null);
  let budgetS = $state(90);
  let dataFeedFault = $derived.by((): { label: "PRICE" | "CVD" | "HISTORY"; error: string | null } => {
    if (err !== null) {
      if (err === cvdErr || /cvd|delta|sub-minute payload|admission|refused/i.test(err)) {
        return { label: "CVD", error: err };
      }
      return { label: "PRICE", error: err };
    }
    if (priceErr !== null) return { label: "PRICE", error: priceErr };
    if (cvdErr !== null) return { label: "CVD", error: cvdErr };
    if (historyError !== null) return { label: "HISTORY", error: historyError };
    return { label: "PRICE", error: null };
  });
  let dataFeedLabel = $derived(dataFeedFault.label);
  let servedState = $derived(
    !loaded ? "LOADING"
      : err ? "ERROR"
        : mayClaimServed({ bars: cvd?.bars.length ?? 0, candles: candles.length })
          ? "SERVED"
          : "PARTIAL",
  );
  const vwapId = (level: VwapLevel): string => {
    if (level.kind === "session") return "vwap:session";
    if (level.kind === "prior-day") return "vwap:prior";
    const anchor = level.kind === "naked-day" ? "D" : level.kind === "naked-week" ? "W" : "M";
    return `vwapNaked:${anchor}:${level.anchor_date}`;
  };
  const vwapLabel = (level: VwapLevel): string => {
    if (level.kind === "session") return "VWAP";
    if (level.kind === "prior-day") return "pd VWAP";
    if (level.kind === "naked-day") return `nD ${level.anchor_date.slice(5)}`;
    return level.kind === "naked-week" ? "nW" : "nM";
  };
  const vwapDash = (level: VwapLevel): "solid" | "dashed" | "dotted" | "double" => {
    if (level.kind === "session") return "solid";
    if (level.kind === "prior-day") return "dashed";
    return level.kind === "naked-day" ? "dotted" : level.kind === "naked-week" ? "dashed" : "double";
  };
  let visibleVwapLevels = $derived((vwapFeed?.levels ?? [])
    .filter((level) => vwapLevelVisible(level, curvePrefs)));
  let nakedLevels = $derived(visibleVwapLevels
    .filter((level) => level.naked)
    .sort((a, b) => Math.abs(a.value - (esLast ?? a.value)) - Math.abs(b.value - (esLast ?? b.value))));
  let drawnVwapLevels = $derived([
    ...visibleVwapLevels.filter((level) => !level.naked),
    ...nakedLevels.slice(0, 6),
  ].map((level) => {
    const toggle = vwapLevelToggle(level);
    const token = vwapCurveToken(CALENDAR_VWAP_CURVE_IDS[toggle]);
    const tokenClass = toggle === "W" ? "weekly" : toggle === "M" ? "monthly" : "daily";
    return { level, id: vwapId(level), label: vwapLabel(level), dash: vwapDash(level), token, tokenClass };
  }));
  let vwapMore = $derived(Math.max(0, nakedLevels.length - 6));
  let vwapOverlayTop = $derived(48);
  let vwapOverlayHeight = $derived(Math.max(260, Math.round((focusHeight ?? height) * 0.61)));
  let vwapReadback = $derived(drawnVwapLevels.map(({ level, id, token }) => ({
    id, series_id: id, token, color: curveColors[token], value: level.value, kind: level.kind, anchor_date: level.anchor_date, naked: level.naked, set_at: level.set_at,
  })));

  const contractMinute = (minute: string | null | undefined): string => {
    if (typeof minute !== "string") return "unknown time";
    const match = minute.match(/T(\d{2}:\d{2})/);
    return match ? `${match[1]}Z` : "unknown time";
  };
  const contractNumber = (value: number | null | undefined): string =>
    typeof value === "number" && Number.isFinite(value) ? value.toFixed(2) : "unknown";
  let contractNote = $derived.by(() => {
    const seam = cvdFeed?.contract_seam;
    if (!seam) return null;
    if (seam.state === "SAME") {
      return `contract - tape ${seam.tape_instrument_id ?? "unknown"} - SAME at ${contractMinute(seam.minute)} `
        + `(${contractNumber(seam.gap_points)} pts, ceiling ${contractNumber(seam.ceiling_points)})`;
    }
    if (seam.state === "SEAM") {
      return `contract - SEAM ${contractNumber(seam.gap_points)} pts at ${contractMinute(seam.minute)} - `
        + "the CVD tape is on another contract than the candles; the line is drawn, the shape is comparable, the basis is not";
    }
    return `contract - UNMEASURED - ${seam.reason ?? "the seam read supplied no reason"}`;
  });

  const wallId = (strike: number): string => `wallChip:${strike}`;
  const wallLabel = (event: WallAlertEvent): string =>
    `${event.level === 2 ? "ABS?" : "WALL?"} ${Math.abs(event.push_contracts / 1000).toFixed(1)}k @${event.wall_es.toFixed(0)}`;
  let wallLevels = $derived((wallFeed?.levels ?? []).slice(0, 3));
  let wallEvents = $derived((wallFeed?.events ?? [])
    .filter((event, index, all) => all.findIndex((candidate) => candidate.wall_strike_spx === event.wall_strike_spx) === index)
    .sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts)));
  let drawnWallEvents = $derived(wallEvents.slice(0, 4));
  let wallMore = $derived(Math.max(0, wallEvents.length - 4));
  let wallReadback = $derived(drawnWallEvents.map((event) => ({
    id: wallId(event.wall_strike_spx), series_id: wallId(event.wall_strike_spx), strike: event.wall_strike_spx,
    wall_es: event.wall_es, level: event.level, label: wallLabel(event), ts: event.ts,
  })));
  const wallPosition = (value: number): number =>
    Math.max(1, Math.min(98, (wallRange.high - value) / Math.max(0.01, wallRange.high - wallRange.low) * 100));
  const wallEventForStrike = (strike: number): WallAlertEvent | undefined =>
    drawnWallEvents.find((event) => event.wall_strike_spx === strike);

  /**
   * ⛔ THE BINDING IS UNTRUSTED WIRE STATE. An off-ladder key is kept verbatim in `rungBad` so
   * the writer's bug reaches the panel; substituting 1m without saying so would draw a chart the
   * owner did not select. `liveRung` exists only so the render and loud refusal stay total.
   */
  let r = $derived(rungFromKey(rung));
  let rungBad = $derived(r === null ? String(rung) : null);
  let liveRung = $derived(r ?? tfRung("1"));
  // RUNG-LABEL-1: the title follows the drawn bars; the clicked rung reads LOADING until its fetch commits.
  let titleRung = $derived(chartTitleRung(liveRung, loadedCandleRung));
  let isSubmin = $derived(liveRung.kind === "submin");
  let chartFreshnessStates = $derived([
    feedState({
      ageS, budgetS, error: err ?? priceErr ?? cvdErr ?? historyError,
      lastBarMs: candles.at(-1)?.t ?? null, barSpanMs: rungSpanMs(liveRung),
    }).state,
    feedState({
      ageS: vwapFeed?.age_s ?? null, budgetS: vwapFeed?.budget_s ?? 90,
      error: vwapError, lastBarMs: null, barSpanMs: 60_000,
      servedVenueState: cashVenueClosed?.verdict ?? null,
    }).state,
    feedState({
      ageS: wallFeed?.age_s ?? null, budgetS: wallFeed?.budget_s ?? 180,
      error: wallError, lastBarMs: null, barSpanMs: 0,
      servedVenueState: wallFeed?.venue_state?.verdict ?? null,
    }).state,
  ]);

  /**
   * The live price pane keeps the server spine intact. A later-window candle is caller-owned until
   * the server publishes that window; a tick inside the newest served window keeps the old replace-
   * last-bar path. The key prevents the stateful prior from being folded twice by one reactive run.
   */
  let serverLast = $derived(candles.at(-1) ?? null);
  $effect(() => {
    const server = serverLast;
    const barMs = rungSpanMs(liveRung);
    const tickKey = [
      server?.t ?? null, server?.o ?? null, server?.h ?? null, server?.l ?? null, server?.c ?? null,
      tickPrice, tickMs, tickFresh, barMs,
    ].join(":");
    if (tickKey === formingTickKey) return;
    formingTickKey = tickKey;
    if (server === null) {
      forming = null;
      return;
    }
    if (formingBarMs !== null && formingBarMs !== barMs) forming = null;
    formingBarMs = barMs;
    if (forming !== null && forming.t <= server.t) {
      // The served bar wins; do not redraw the tape's provisional candle over it.
      forming = null;
      return;
    }
    const next = formingBar({
      bar: server,
      prior: forming,
      price: tickPrice,
      priceMs: tickMs,
      fresh: tickFresh,
      barMs,
    });
    if (next?.forming === true) forming = next as FormingCandle;
  });
  let liveLast = $derived(formingBar({
    bar: serverLast,
    prior: forming,
    price: tickPrice,
    priceMs: tickMs,
    // The shared tick is the TAPE tail. Under a measured seam it cannot extend the spine's
    // final candle: that would draw a phantom price on another contract. The settled spine
    // candle remains; only the cross-contract tail is refused by name.
    fresh: tickFresh && cvdFeed?.contract_seam?.state !== "SEAM",
    barMs: rungSpanMs(liveRung),
  }));
  let liveIsForming = $derived(
    liveLast !== null && serverLast !== null && liveLast.forming === true && liveLast.t > serverLast.t,
  );
  /**
   * The SAME mapping the ES1! panes use — one implementation, so the two charts cannot drift.
   * f54430c6 (R3113): the level DISTANCE reads the close the pane DRAWS (liveLast), one fact and one
   * hop; it read the served spine's close while the pane drew the tick's. The basis RATIO (levelRatio,
   * above) stays on the served pair and moves only at refetch, so a tick never moves a wall.
   */
  let renderLevels = $derived(chartLevelsFromDealer(chartLevels, liveLast?.c ?? esLast, levelScopeToken));
  /** The ratio's as-of, behind the info glyph: the ES bar it read, and that the spot's own time is not served. */
  let basisAsOf = $derived(
    levelRatio === null || serverLast === null
      ? null
      : `BASIS · ES ${serverLast.c.toFixed(2)} (bar opened ${new Date(serverLast.t).toISOString().slice(11, 19)}Z) / SPX ${levels0dte?.spot} · ratio ${levelRatio.toFixed(5)} · the spot's own time is not served (receipt ${levels0dte?.receipt_ts ?? "absent"})`,
  );
  $effect(() => {
    const last = liveLast;
    if (last === null) return;
    writeLiveMeasurement(primarySection, {
      t: last.t,
      close: last.c,
      forming: liveIsForming,
      tickMs,
      rung: rungKey(liveRung),
    });
  });
  let liveCandles = $derived(
    candles.length && liveLast !== null
      ? liveLast.forming === true && liveLast.t > serverLast!.t
        ? [...candles, liveLast]
        : [...candles.slice(0, -1), liveLast]
      : candles,
  );
  $effect(() => {
    const root = primarySection;
    const minute = tideCursorMinute;
    const bars = liveCandles;
    const spanMs = rungSpanMs(liveRung);
    if (root === null) return;
    void tick().then(() => {
      if (primarySection !== root || tideCursorMinute !== minute) return;
      const index = minute === null ? null : candleForMinute(bars.map((bar) => bar.t), spanMs, minute);
      if (index === null) clearChartTimeCursor(root);
      else setChartTimeCursor(root, Math.floor(bars[index].t / 1_000), bars[index].c);
    });
  });
  $effect(() => {
    if (candleRoot === null && candleServedRung === null) return;
    onFocusData?.({
      cvd,
      cvdFeed,
      cvdBlocked: cvdErr,
      priceBlocked: priceErr,
      price,
      candles: liveCandles,
      candleRoot,
      candleServedRung,
      levels: levels0dte,
      levelsError: levels0dteError,
      rangeFeed,
      ageS,
      budgetS,
    });
  });
  let calendarCurveEnabled = $derived(curvePrefs.session || curvePrefs.D || curvePrefs.W || curvePrefs.M);
  let curveSourceCandles = $derived(calendarCurveEnabled ? curveCandles : liveCandles);
  let wallRange = $derived((candles.length ? candles : liveCandles).reduce(
    (range, candle) => ({ low: Math.min(range.low, candle.l), high: Math.max(range.high, candle.h) }),
    { low: Number.POSITIVE_INFINITY, high: Number.NEGATIVE_INFINITY },
  ));
  let vwapRange = $derived((candles.length ? candles : liveCandles).reduce(
    (range, candle) => ({ low: Math.min(range.low, candle.l), high: Math.max(range.high, candle.h) }),
    { low: Number.POSITIVE_INFINITY, high: Number.NEGATIVE_INFINITY },
  ));
  let sessionCurveAnchor = $derived(liveCandles.length ? cmeSessionAnchor(liveCandles.at(-1)!.t) : null);
  let curveSet = $derived(buildVwapCurves(curveSourceCandles, {
    sessionAnchorTs: sessionCurveAnchor ?? undefined,
    latestTs: liveCandles.at(-1)?.t,
    coverageFromTs: curveCoverageFrom ?? undefined,
    renderWindows: calendarCurveEnabled && curveCandles.length
      ? liveCandles.map((candle) => ({ t: candle.t, to: candle.t + rungSpanMs(liveRung) }))
      : undefined,
    pulledAnchors,
    visible: curvePrefs,
  }));
  let curveReadback = $derived(curveSet.drawn.map((curve) => ({
    id: curve.id,
    series_id: curve.id,
    token: vwapCurveToken(curve.id),
    color: curveColors[vwapCurveToken(curve.id)],
    anchor_ts: curve.anchorTs,
    points: curve.points.map((point) => ({
      ...point,
      recomputed: point.value === null ? null : point.numerator / point.denominator,
    })),
  })));
  let curveOwnerIds = $derived([
    ...Object.values(CALENDAR_VWAP_CURVE_IDS),
    ...pulledAnchors.map((anchor) => `${PULLED_AVWAP_CURVE_PREFIX}${anchor}`),
  ]);
  /** Non-reactive receipt key: an unchanged paging boundary must not flood the console each poll. */
  let loggedIncompleteCurves = "";

  $effect(() => {
    const ids = curveSet.incomplete.join(",");
    if (ids === loggedIncompleteCurves) return;
    loggedIncompleteCurves = ids;
    if (ids.length > 0) {
      console.error(`[PrimaryChartPanel] ${slot || chartRole} VWAP CURVE INCOMPLETE — ${ids}`);
    }
  });

  $effect(() => {
    const root = primarySection;
    const colors = curveColors;
    const curves = curveSet.drawn.map((curve) => ({
      id: curve.id, points: curve.points, color: colors[vwapCurveToken(curve.id)],
    }));
    const retainedIds = curveOwnerIds;
    if (root === null || Object.values(colors).some((color) => color.length === 0)) return;
    let prerequisite = "native chart";
    // Host existence follows the first network response, so it has a structural readiness signal;
    // the armed frame loop remains for native-chart / pane registration after that signal.
    return attachWhenHostReady({
      retry: attachWithRetry,
      attach: () => syncVwapCurveSeries(root, curves, retainedIds, (refusal) => {
        prerequisite = refusal.prerequisite;
      }),
      maxRetries: VWAP_SYNC_MAX_RETRIES,
      schedule: (callback) => requestAnimationFrame(callback),
      cancel: (handle) => cancelAnimationFrame(handle),
      onRefused: (framesSpent) => {
        console.error(
          `[PrimaryChartPanel] ${slot || chartRole} VWAP CURVE REFUSED — ${prerequisite} unavailable ` +
          `after ${framesSpent} animation frames`,
        );
      },
      observeHost: (onReady) => observePositiveChartHost(root, onReady),
      hostTimeoutMs: VWAP_HOST_READY_TIMEOUT_MS,
      scheduleTimeout: (callback, delayMs) => window.setTimeout(callback, delayMs),
      cancelTimeout: (handle) => window.clearTimeout(handle),
      onHostRefused: (waitedMs) => {
        console.error(
          `[PrimaryChartPanel] ${slot || chartRole} VWAP CURVE REFUSED — chart host unavailable ` +
          `after ${waitedMs}ms readiness bound`,
        );
      },
    });
  });

  function curveAnchor(key: Exclude<VwapCurveToggle, "avwap">, latest: number): number {
    return key === "session" ? cmeSessionAnchor(latest) : calendarVwapAnchor(key, latest);
  }

  function ensureVisibleCurveCoverage(selected: Rung = liveRung): Promise<void> {
    if (curveCoverageFlight !== null) {
      curveCoveragePending = true;
      return curveCoverageFlight;
    }
    const enabled = (["session", "D", "W", "M"] as const).filter((key) => curvePrefs[key]);
    const latest = liveCandles.at(-1)?.t;
    if (enabled.length === 0 || latest === undefined) return Promise.resolve();
    const anchors = [...enabled.map((key) => curveAnchor(key, latest))];
    if (curvePrefs.avwap) anchors.push(...pulledAnchors);
    const anchor = Math.min(...anchors);
    const retentionProbeFrom = anchor - 7 * 24 * 60 * 60_000;
    const coverageRung = tfRung("1");
    const targetTo = vwapCoverageTargetTo(selected, latest);
    const priorCoverageFrom = curveCoverageFrom;
    const priorCoverageTo = curveCoverageTo;
    let refreshFrom: number | null = null;
    let urls: string[];
    try {
      if (priorCoverageFrom === null || priorCoverageTo === null) {
        urls = vwapCurveCoverageUrls(coverageRung, retentionProbeFrom, targetTo);
      } else {
        refreshFrom = Math.max(priorCoverageFrom, priorCoverageTo - VWAP_REFRESH_OVERLAP_MS);
        urls = [
          ...(anchor < priorCoverageFrom
            ? vwapCurveCoverageUrls(coverageRung, retentionProbeFrom, priorCoverageFrom)
            : []),
          ...vwapCurveCoverageUrls(coverageRung, refreshFrom, targetTo),
        ];
      }
    } catch (error) {
      curveCoverageError = error instanceof Error ? error.message : String(error);
      console.error(`[PrimaryChartPanel] ${chartId} VWAP CURVE COVERAGE ERROR — ${curveCoverageError}`);
      return Promise.resolve();
    }
    if (urls.length === 0) {
      curveCoverageError = null;
      return Promise.resolve();
    }
    curveCoverageFlight = (async () => {
      let stagedCurveCandles = curveCandles;
      let sawNewTailBar = priorCoverageTo === null;
      for (const url of urls) {
        const response = await leg(`${api}${url}`, "primary-vwap-curve-history");
        if (response.transport !== null) throw new Error(response.transport);
        const request = new URL(url, "http://spx-hub.local");
        const from = Number(request.searchParams.get("from"));
        const to = Number(request.searchParams.get("to"));
        const allowEmptyTail = refreshFrom !== null && from >= refreshFrom;
        const read = readVwapCoveragePage(response.body, coverageRung, from, to, "ES", allowEmptyTail);
        if (read.page === null) throw new Error(read.blocked);
        if (priorCoverageTo !== null && read.page.bars.some((bar) => bar.t >= priorCoverageTo)) {
          sawNewTailBar = true;
        }
        stagedCurveCandles = mergeCandlePages(stagedCurveCandles, read.page.bars) as Candle[];
      }
      if (!stagedCurveCandles.some((candle) => candle.t <= anchor)) {
        throw new Error(`retained native history does not prove the VWAP anchor ${anchor}`);
      }
      curveCandles = stagedCurveCandles;
      curveCoverageFrom = priorCoverageFrom === null ? anchor : Math.min(priorCoverageFrom, anchor);
      if (priorCoverageTo === null || targetTo <= priorCoverageTo || sawNewTailBar) {
        curveCoverageTo = priorCoverageTo === null ? targetTo : Math.max(priorCoverageTo, targetTo);
      }
      curveCoverageError = null;
    })().catch((error) => {
      curveCoverageError = error instanceof Error ? error.message : String(error);
      console.error(`[PrimaryChartPanel] ${chartId} VWAP CURVE COVERAGE ERROR — ${curveCoverageError}`);
    }).finally(() => {
      const rerun = curveCoveragePending;
      curveCoveragePending = false;
      curveCoverageFlight = null;
      if (rerun && r !== null) void ensureVisibleCurveCoverage(r);
    });
    return curveCoverageFlight;
  }

  function toggleCurve(key: VwapCurveToggle) {
    curvePrefs = { ...curvePrefs, [key]: !curvePrefs[key] };
    curvePrefsError = rememberVwapCurvePrefs(chartId, curvePrefs);
    if (curvePrefs[key] && (key !== "avwap" || calendarCurveEnabled)) void ensureVisibleCurveCoverage();
  }

  function removePulledAnchor(anchor: number) {
    pulledAnchors = pulledAnchors.filter((candidate) => candidate !== anchor);
  }

  function armAnchorGesture(event: PointerEvent) {
    if (event.button !== 0 || !curvePrefs.avwap || !(event.target instanceof Element)) return;
    const host = event.target.closest(".host");
    if (!(host instanceof HTMLElement)) return;
    anchorGesture = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
  }

  function finishAnchorGesture(event: PointerEvent) {
    const gesture = anchorGesture;
    anchorGesture = null;
    if (event.button !== 0 || !gesture || gesture.pointerId !== event.pointerId || !curvePrefs.avwap) return;
    if (Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 6) return;
    const wanted = chartTimeAtClientX(primarySection, event.clientX);
    if (wanted === null) return;
    const hit = anchorBarAtTime(liveCandles, wanted, rungSpanMs(liveRung));
    if (hit !== null && !pulledAnchors.includes(hit.t)) {
      pulledAnchors = [...pulledAnchors, hit.t];
      if (calendarCurveEnabled) void ensureVisibleCurveCoverage();
    }
  }

  /**
   * ⛔ FEED BEFORE CONTROL. Minute rungs are the two routes this panel already drives; a
   * sub-minute pill appears only after that exact span answered with a payload `readRungPayload`
   * recognises. `null` means the probes are still outstanding, never that no span exists.
   */
  let subminServed = $state<Rung[] | null>(null);
  let offeredRungs = $derived(rungOptions([
    ...RUNG_LADDER.filter((candidate) => candidate.kind === "tf"),
    ...(subminServed ?? []),
  ]));

  /**
   * The probe asks the real route once per declared span. A transport failure is already logged
   * by `leg`; an unreadable answer is withheld because a 200 carrying the SPA shell is not feed
   * capability. The control advertises only the spans whose actual envelope survived the reader.
   */
  async function probeSubmin() {
    const candidates = RUNG_LADDER.filter((item) => item.kind === "submin");
    const probed = await Promise.all(candidates.map(async (candidate): Promise<Rung | null> => {
      const candleResponse = await leg(
        `${api}${candleRungUrl(candidate, 2)}`,
        "primary-rung-probe-price",
      );
      const candlePage = candleResponse.transport === null
        ? readCandlePage(candleResponse.body, candidate, "ES").page
        : null;
      if (candlePage?.sessionVerdict === "CLOSED") {
        return candidate;
      }
      const cvdResponse = await leg(
        `${api}${rungUrl(candidate, 2, "ES")}`,
        "primary-rung-probe-cvd",
      );
      const cvdPayload = cvdResponse.transport === null
        ? readRungPayload(cvdResponse.body, candidate)
        : null;
      return cvdPayload && candlePage ? candidate : null;
    }));
    subminServed = probed.filter((candidate): candidate is Rung => candidate !== null);
  }

  /**
   * ⛔ EVERY PILL MAKES `load` RE-ENTRANT. The generation is taken before any await and checked
   * once at commit, so a slow response for the rung the owner just left cannot repaint either
   * pane under the newly-selected header. Price, candles and CVD therefore move as one frame.
   */
  let feedGen = 0;
  let firstPaintReady = false;
  let candlePollInFlight = false;
  let pollsSkipped = $state(0);
  /**
   * ⛔ ITS OWN LEG, ON ITS OWN CLOCK — R814① puts the lines on the DEALER's 3m budget, not the
   * chart's 90s one. Folding it into `load()` would retire the levels every time the rung changes
   * and re-fetch them at the price cadence: two different freshness claims from one call.
   * ⚠ THE REFUSAL LIVES IN `loadZeroDte`, so a transport failure cannot be substituted with another
   * source here — the same loader the ES1! panes drive, not a second implementation.
   */
  async function loadLevels() {
    const requestKey = "/api/dealer/levels";
    const st = await loadZeroDte(fetchDealerLevels);
    if (st.levels !== null) {
      levels0dte = st.levels;
      levelsFetchedAtMs = Date.now();
      levelsRequestKey = requestKey;
    } else {
      levels0dte = keptPayloadAfterFailure(levels0dte, levelsRequestKey, requestKey);
      if (levels0dte === null) {
        levelsFetchedAtMs = null;
        levelsRequestKey = null;
      }
    }
    levels0dteError = st.fault?.label ?? st.error;
    if (st.fault) console.error(st.fault.label);
  }

  async function loadWalls() {
    if (chartRole !== "lower") return;
    try {
      const response = await fetchWallAlerts(50);
      if (response.root !== undefined && response.root !== "SPX") throw new Error(`wall route returned root=${response.root}`);
      wallFeed = response;
      wallFetchedAtMs = Date.now();
      wallRequestKey = wallDoorKey;
      const state = wallDisplayState(response, null);
      wallError = state === "error" ? (response.reason ?? response.state ?? "wall alert feed refused") : null;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      if (wallDisplayState(null, message) === "no-data") {
        // The door's typed refusal is its current answer, not a failed fetch: old walls never stand under it (law 1).
        wallError = null;
        wallFeed = { ok: false, state: "EMPTY", reason: message, levels: [], events: [] };
        wallFetchedAtMs = null;
        wallRequestKey = null;
      } else {
        wallFeed = keptPayloadAfterFailure(wallFeed, wallRequestKey, wallDoorKey);
        wallError = message;
        console.error(`[PrimaryChartPanel] wall alerts refused — ${wallError}`);
      }
    }
  }

  /** ORDER 20B capability read. It shares the chart poll cadence; it owns no extra timer. */
  async function loadRange(nextTicks: 8 | 12 = rangeTicks) {
    if (chartRole !== "lower" || !rangeEnabled) return;
    const mine = ++rangeGen;
    const response = await leg(`${api}/api/es/cvd/range?ticks=${nextTicks}&limit=300`, "primary-range", RANGE_CADENCE_MS);
    if (mine !== rangeGen) return;
    if (response.transport !== null) {
      console.error(`[PrimaryChartPanel] range-bar feed failed — ${response.transport}`);
      rangeFeed = { ok: false, reason: response.transport };
      return;
    }
    rangeFeed = response.body as RangeFeed;
    rangeTicks = nextTicks;
  }

  async function load(selected: Rung = liveRung) {
    const mine = ++feedGen;
    let nextCvd: DrawableCvd | null = null;
    let nextCvdFeed: Cvd2Feed | null = null;
    let nextCandles: typeof candles = [];
    let nextDeclaredResidue: readonly ResidueEntry[] = [];
    let nextCandleRoot: string | null = null;
    let nextCandleServedRung: string | null = null;
    let nextCandleVenueState: VenueState | null = null;
    let nextCandlePageRead = false;
    let nextCvdErr: string | null = null;
    let nextPriceErr: string | null = null;
    let nextErr: string | null = null;
    let nextAgeS: number | null = null;
    let nextBudgetS = 90;
    let nextHasMore = false;
    let nextBefore: number | null = null;

    try {
      // ORDER 25 S1 made sub-minute PRICE a first-class /api/es/candles rung. CVD keeps its
      // tick-derived route; both requests share this generation and commit as one frame.
      if (rungSource(selected).oneRow) {
        const [response, candleResponse] = await Promise.all([
          leg(`${api}${rungUrl(selected, limit, "ES")}`, "primary-cvd"),
          leg(`${api}${candleRungUrl(selected, limit)}`, "primary-price"),
        ]);
        if (response.transport !== null) {
          nextCvdErr = response.transport;
          throw new Error(response.transport);
        }
        if (candleResponse.transport !== null) {
          nextPriceErr = candleResponse.transport;
          throw new Error(candleResponse.transport);
        }
        const payload = readRungPayload(response.body, selected);
        if (payload === null) {
          nextCvdErr = `${rungLabel(selected)}: unreadable sub-minute payload`;
          throw new Error(`${rungLabel(selected)}: unreadable sub-minute payload`);
        }
        const candleRead = readCandlePage(candleResponse.body, selected, "ES");
        if (candleRead.page === null) {
          nextPriceErr = candleRead.blocked;
          throw new Error(candleRead.blocked);
        }
        const candleError = candlePagePriceError(candleRead.page);
        if (candleError !== null) {
          nextPriceErr = `${rungLabel(selected)}: ${candleError}`;
          throw new Error(nextPriceErr);
        }
        nextCandles = candleRead.page.bars.map((bar) => ({ ...bar }));
        nextDeclaredResidue = candleRead.page.declaredResidue;
        nextCandleRoot = candleRead.page.root;
        nextCandleServedRung = candleRead.page.servedKey;
        nextCandleVenueState = candleRead.page.venueState as VenueState | null;
        nextCandlePageRead = true;
        nextAgeS = candleRead.page.ageS;
        nextBudgetS = candleRead.page.budgetS ?? 90;
        const verdict = admitSubmin(payload);
        // ⛔ BOTH HALVES. The producer's refusal is authoritative and travels verbatim; an adapter
        // that rebuilds data around a refusal is a hole in the guard, not another reader.
        if (verdict.blocked !== null || verdict.inputs === null) {
          nextCvdErr = verdict.blocked ?? "refused";
          throw new Error(verdict.blocked ?? "refused");
        }

        const cvdFeed: Cvd2Feed = {
          ...verdict.inputs.cvdFeed,
          tf_applied: payload.appliedMatches === true ? rungKey(selected) : payload.appliedEcho ?? undefined,
          budget_s: payload.env.budget_s ?? 180,
          // ⛔ `subminCvdFeed` deliberately does not assert this register. Derive it from the
          // admitted rows: a finite zero is PRESENT, while an all-null lane is explicitly EMPTY.
          cvd_state: verdict.inputs.cvdFeed.bars.some((bar) => Number.isFinite(bar.cvd as number))
            ? "PRESENT"
            : "EMPTY",
        };
        const admission = admitCvd(cvdFeed, null, "ES");
        nextCvd = admission.drawable;
        nextCvdErr = admission.blocked;
        nextCvdFeed = admission.drawable === null ? null : cvdFeed;
      } else {
        const tf = selected.kind === "tf" ? selected.tf : "1";
        const [cvdRes, cndRes] = await Promise.all([
          leg(`${api}/api/es/cvd?tf=${tf}`, "primary"),
          leg(`${api}${candleRungUrl(selected, limit)}`, "primary-price"),
        ]);

        /**
         * ⛔ ONE CALL, AND ITS RESULT IS THE ONLY WAY TO GET SOMETHING THE PANE ACCEPTS.
         * `admitCvd` is armed by eleven assertions and its docblock says *"a blank pane is the
         * one outcome this function exists to prevent."* The LWC rewrite once KEPT THIS HELPER
         * AND DROPPED THE CALL SITE and the suite stayed green, because it arms the FUNCTION.
         * ⇒ ***A PURE-MODULE SUITE CERTIFIES THE FUNCTION, NOT THE PROGRAM.***
         */
        const rawCvd = cvdRes.body as Cvd2Feed | null;
        const admission = admitCvd(rawCvd, cvdRes.transport, "ES");
        nextCvd = admission.drawable;
        nextCvdErr = admission.blocked;
        nextCvdFeed = admission.drawable === null ? null : rawCvd;

        if (cndRes.transport !== null) {
          nextPriceErr = cndRes.transport;
        } else {
          const candleRead = readCandlePage(cndRes.body, selected, "ES");
          if (candleRead.page === null) {
            nextPriceErr = candleRead.blocked;
            console.error(`[PrimaryChartPanel] ${rungLabel(selected)} PRICE ERROR — ${nextPriceErr}`);
          } else {
            nextPriceErr = candlePagePriceError(candleRead.page);
            if (nextPriceErr !== null) {
              console.error(`[PrimaryChartPanel] ${rungLabel(selected)} PRICE ERROR — ${nextPriceErr}`);
            }
            nextCandles = candleRead.page.bars.map((bar) => ({ ...bar }));
            nextDeclaredResidue = candleRead.page.declaredResidue;
            nextCandleRoot = candleRead.page.root;
            nextCandleServedRung = candleRead.page.servedKey;
            nextCandleVenueState = candleRead.page.venueState as VenueState | null;
            nextCandlePageRead = true;
            nextAgeS = candleRead.page.ageS;
            nextBudgetS = candleRead.page.budgetS ?? 90;
            nextHasMore = candleRead.page.hasMore;
            nextBefore = candleRead.page.nextBefore;
          }
        }
      }
    } catch (e) {
      nextErr = e instanceof Error ? e.message : String(e);
      console.error(`[PrimaryChartPanel] ${rungLabel(selected)} load refused — ${nextErr}`);
    }

    if (mine !== feedGen) return;
    const selectedKey = rungKey(selected);
    const sameRung = loadedCandleRung === selectedKey;
    // ORDER 44: the accumulated history survives a poll on the SAME rung and is dropped on a
    // rung change, exactly as the candle rail's merge already does one line down. A rung change
    // invalidates the cursor as well as the bars — a 12m cursor spent at 3m would 400.
    const keptCvdHistory = sameRung ? cvdOlderBars : [];
    const drawnCvdFeed = withCvdHistory(nextCvdFeed, keptCvdHistory);
    const historyAdmission = drawnCvdFeed === null ? null : admitCvd(drawnCvdFeed, null, "ES");
    cvd = historyAdmission === null ? nextCvd : historyAdmission.drawable;
    cvdFeed = historyAdmission?.drawable === undefined || historyAdmission?.drawable === null
      ? nextCvdFeed
      : drawnCvdFeed;
    if (historyAdmission !== null && historyAdmission.blocked !== null) {
      nextCvdErr = historyAdmission.blocked;
    }
    const committedCandles = sameRung ? mergeCandlePages(candles, nextCandles) : nextCandles;
    price = committedCandles.map((bar) => ({ t: bar.t, c: bar.c }));
    candles = committedCandles;
    const sameRoot = !nextCandlePageRead || candleRoot === nextCandleRoot;
    declaredResidue = mergeDeclaredResidue(sameRung && sameRoot ? declaredResidue : [], nextDeclaredResidue);
    if (nextCandlePageRead) {
      candleRoot = nextCandleRoot;
      candleServedRung = nextCandleServedRung;
      candleVenueState = nextCandleVenueState;
      candleVenueStateKnown = true;
      setServedVenueState(nextCandleVenueState);
    } else {
      // A failed refresh cannot leave yesterday's CLOSED verdict suppressing today's live faults.
      candleVenueState = null;
      candleVenueStateKnown = true;
      setServedVenueState(null);
    }
    cvdErr = nextCvdErr;
    priceErr = nextPriceErr;
    err = nextErr;
    ageS = nextAgeS;
    budgetS = nextBudgetS;
    dataPollSeq += 1;
    if (!sameRung) {
      loadedCandleRung = selectedKey;
      const liveOldestCandle = nextCandles[0]?.t ?? null;
      candleNextBefore = nextBefore ?? liveOldestCandle;
      candleHasMore = selected.kind === "tf" && liveOldestCandle !== null &&
        (canPageDeepCandles(selected) ? nextHasMore : true);
      cvdOlderBars = [];
      cvdNextBefore = null;
      cvdHasMore = false;
      historyDepth = 0;
      historyError = null;
      historyEpoch += 1;
      historyGestureArmed = false;
      historyGestureAt = 0;
    } else if (historyDepth === 0) {
      candleNextBefore = nextBefore;
      candleHasMore = nextHasMore;
    }
    if (historyDepth === 0) {
      // ⛔ THE FIRST CVD CURSOR IS DERIVED, NOT SERVED. The ANCHORED response carries no
      // `next_before` — it answers "the newest N", a question with no continuation — so the
      // client seeds the cursor from the OLDEST BAR IT WAS GIVEN. That is exactly the value the
      // cursor door would have handed back for this window, and it is exclusive, so the first
      // history page adjoins the live page with neither a repeat nor a gap.
      const liveOldestCvd = nextCvdFeed?.bars?.[0]?.t ?? null;
      cvdNextBefore = typeof liveOldestCvd === "number" ? liveOldestCvd : null;
      cvdHasMore = cvdNextBefore !== null && canPageDeepCvd(selected);
    }
    loaded = true;
    if (firstPaintReady) void ensureVisibleCurveCoverage(selected);
    if (chartRole === "lower" && rangeEnabled) void loadRange();
  }

  async function loadOlderCandles(
    requested: Rung | null = r,
    purpose: "gesture" | "eager" = "gesture",
  ): Promise<HistoryPageResult> {
    const selected = requested;
    if (selected === null || loadedCandleRung !== rungKey(selected) ||
        !canPageAlignedCandles(selected) || !candleHasMore ||
        candleNextBefore === null) return "exhausted";
    if (purpose === "eager" && (!cvdHasMore || cvdNextBefore === null)) return "exhausted";
    if (historyLoading) return "stale";
    const epoch = historyEpoch;
    // One shared exclusive boundary keeps price and CVD on one axis even if a sparse page made
    // one route's oldest bar earlier than the other's. Re-reading an overlap is safe (merge is
    // keyed by t); skipping past the shallower lane is not.
    const before = purpose === "eager" && cvdNextBefore !== null
      ? Math.max(candleNextBefore, cvdNextBefore)
      : candleNextBefore;
    // ⛔ ORDER 44: THE TWO LEGS TRAVEL IN ONE FRAME. ORDER 48 extends the candle half at native
    // 1m through its bounded range door, so eager fills can keep price and CVD on one time span.
    // Gesture retries retain ORDER 44's independent-leg commit; eager pages are transactional.
    const cvdBefore = cvdHasMore && canPageDeepCvd(selected)
      ? purpose === "eager" ? before : cvdNextBefore
      : null;
    // ⛔ THE CVD URL IS BUILT OUTSIDE THE TRY, ON PURPOSE. `cvdPageUrl` THROWS on a cursor it
    // cannot express, and a throw raised inside the shared `try` would be caught by the PRICE
    // leg's handler and abort the price prepend — one lane's refusal silently cancelling the
    // other's commit. Measured on this exact code path: the whole pan became a no-op and the
    // arm read "the price series did not grow", which points at the wrong lane entirely.
    // ⇒ ***A SUBORDINATE LEG MUST NOT BE ABLE TO THROW THROUGH THE PRIMARY ONE.***
    let candleHistoryUrl: string;
    try {
      candleHistoryUrl = `${api}${alignedCandleHistoryUrl(selected, CVD_PAGE_LIMIT, before)}`;
    } catch (error) {
      historyError = error instanceof Error ? error.message : String(error);
      console.error(`[PrimaryChartPanel] HISTORY CURSOR ERROR — ${rungLabel(selected)} before=${before}: ${historyError}`);
      return "failed";
    }
    let cvdHistoryUrl: string | null = null;
    let cvdUrlError: string | null = null;
    if (cvdBefore !== null) {
      try {
        cvdHistoryUrl = `${api}${cvdPageUrl(selected, CVD_PAGE_LIMIT, cvdBefore)}`;
      } catch (error) {
        cvdUrlError = error instanceof Error ? error.message : String(error);
      }
    }
    historyLoading = true;
    historyError = null;
    try {
      const [response, cvdResponse] = await Promise.all([
        leg(candleHistoryUrl, "primary-price-history"),
        cvdHistoryUrl === null
          ? Promise.resolve(null)
          : leg(cvdHistoryUrl, "primary-cvd-history"),
      ]);
      if (response.transport !== null) throw new Error(response.transport);
      const read = readCandlePage(response.body, selected, "ES");
      if (read.page === null) throw new Error(read.blocked);
      let cvdRead: ReturnType<typeof readCvdPage> | null = null;
      let cvdLegError: string | null = cvdUrlError;
      if (cvdResponse !== null) {
        if (cvdResponse.transport !== null) {
          cvdLegError = cvdResponse.transport;
        } else {
          cvdRead = readCvdPage(cvdResponse.body, selected, "ES");
          if (cvdRead.page === null) cvdLegError = cvdRead.blocked;
        }
      }
      if (purpose === "eager" && cvdLegError !== null) throw new Error(cvdLegError);
      if (epoch !== historyEpoch || loadedCandleRung !== rungKey(selected)) return "stale";
      if (read.page.bars.length === 0 || (purpose === "eager" && cvdRead?.page?.bars.length === 0)) {
        candleHasMore = false;
        if (purpose === "eager") cvdHasMore = false;
        return "exhausted";
      }
      // 5a26f4b5: preserve the window LOGICALLY, anchored on the newest served bar (it survives the
      // prepend). A time range cannot hold the gutter, and restoring one clipped it to 0 every load.
      const preservedRange = candles.length === 0
        ? null
        : captureVisibleLogicalAnchor(primarySection, Math.floor(candles[candles.length - 1].t / 1000) as never);
      const merged = mergeCandlePages(read.page.bars, candles);
      candles = merged;
      declaredResidue = mergeDeclaredResidue(declaredResidue, read.page.declaredResidue);
      price = merged.map((bar) => ({ t: bar.t, c: bar.c }));
      if (cvdRead?.page != null) {
        cvdOlderBars = mergeCvdPages(cvdRead.page.bars, cvdOlderBars);
        const drawn = withCvdHistory(cvdFeed, cvdOlderBars);
        const admission = drawn === null ? null : admitCvd(drawn, null, "ES");
        if (admission?.drawable != null) {
          cvd = admission.drawable;
          cvdFeed = drawn;
        }
      }
      const candleContinuation = read.page.nextBefore ?? read.page.bars[0]?.t ?? null;
      const cvdContinuation = cvdRead?.page?.nextBefore ?? cvdRead?.page?.bars[0]?.t ?? null;
      const sharedContinuation = candleContinuation === null || cvdContinuation === null
        ? null
        : Math.max(candleContinuation, cvdContinuation);
      const sharedHasMore = selected.kind === "tf" && selected.tf === "1"
        ? cvdRead?.page?.hasMore === true
        : read.page.hasMore && cvdRead?.page?.hasMore === true;
      if (purpose === "eager" && cvdRead?.page != null) {
        candleNextBefore = sharedContinuation;
        cvdNextBefore = sharedContinuation;
        candleHasMore = sharedHasMore && sharedContinuation !== null;
        cvdHasMore = sharedHasMore && sharedContinuation !== null;
      } else {
        // ORDER 44 gesture compatibility: each successful leg advances its own continuation;
        // a subordinate CVD refusal stays loud and remains retryable without cancelling price.
        candleNextBefore = candleContinuation;
        candleHasMore = read.page.hasMore && candleContinuation !== null;
        if (cvdRead?.page != null) {
          cvdNextBefore = cvdContinuation;
          cvdHasMore = cvdRead.page.hasMore && cvdContinuation !== null;
        }
      }
      if (cvdLegError !== null) {
        console.error(`[PrimaryChartPanel] CVD HISTORY ERROR — ${rungLabel(selected)} before=${cvdBefore}: ${cvdLegError}`);
      }
      historyError = cvdLegError;
      historyDepth += 1;
      // SessionCvdPane is owned by the stacked 20C chain and deliberately remains untouched.
      // Its span-change fit runs during this tick; restore the user's pre-prepend window after it.
      await tick();
      restoreVisibleLogicalAnchor(primarySection, preservedRange);
      return cvdLegError === null ? "loaded" : "failed";
    } catch (error) {
      if (epoch !== historyEpoch) return "stale";
      historyError = error instanceof Error ? error.message : String(error);
      console.error(`[PrimaryChartPanel] HISTORY ERROR — ${rungLabel(selected)} before=${before}: ${historyError}`);
      return "failed";
    } finally {
      if (epoch === historyEpoch) historyLoading = false;
    }
  }

  async function eagerFillHistory(selected: Rung = liveRung) {
    const epoch = historyEpoch;
    const pageBudget = eagerHistoryPageBudget(candles.length);
    await runSequentialHistoryFill(pageBudget, async () => {
      if (epoch !== historyEpoch || loadedCandleRung !== rungKey(selected)) return "stale";
      if (!needsEagerHistory(
        selected,
        candles.length,
        candles.at(0)?.t ?? null,
        candles.at(-1)?.t ?? null,
        historyDepth,
      )) return "exhausted";
      return loadOlderCandles(selected, "eager");
    });
  }

  function handleVisibleWindowChange(window: { from: number; to: number } | null) {
    onVisibleWindowChange?.(window);
    const recentGesture = historyGestureArmed && Date.now() - historyGestureAt < 1_500;
    if (historyGestureArmed && !recentGesture) historyGestureArmed = false;
    const loadedOldest = candles.at(0)?.t ?? null;
    const loadedNewest = candles.at(-1)?.t ?? null;
    const edgePan = shouldLoadOlderCandles(window, loadedOldest, liveRung);
    const zoomedPastDepth = shouldFetchForEmptyRange(window, loadedOldest, loadedNewest);
    if (recentGesture && (edgePan || zoomedPastDepth)) {
      historyGestureArmed = false;
      void loadOlderCandles();
    }
  }

  function ownsChartTarget(event: Event): boolean {
    const card = event.currentTarget;
    const host = event.target instanceof Element ? event.target.closest(".host") : null;
    return card instanceof Element && host !== null && card.contains(host);
  }

  function armHistoryGesture(event: Event) {
    if (ownsChartTarget(event)) {
      historyGestureArmed = true;
      historyGestureAt = Date.now();
      if (event instanceof PointerEvent) armAnchorGesture(event);
    }
  }

  onMount(() => {
    const stopTideCursor = sharedTideCursor.subscribe((minute) => (tideCursorMinute = minute));
    const stopChartCursorReady = primarySection !== null
      ? observePositiveChartHost(primarySection, () => {
        if (primarySection === null) return;
        const host = primarySection.querySelector<HTMLElement>(".host");
        if (host !== chartCursorHost) {
          stopChartUserCrosshair?.();
          stopChartUserCrosshair = null;
          chartCursorHost = host;
        }
        if (stopChartUserCrosshair === null) {
          stopChartUserCrosshair = onChartUserCrosshair(primarySection, (tSec) => {
            if (chartRole === "lower") sharedTideCursor.set(tSec === null ? null : tSec * 1_000);
            else if (tSec === null && primarySection !== null) {
              const index = tideCursorMinute === null
                ? null
                : candleForMinute(liveCandles.map((bar) => bar.t), rungSpanMs(liveRung), tideCursorMinute);
              if (index === null) clearChartTimeCursor(primarySection);
              else setChartTimeCursor(primarySection, Math.floor(liveCandles[index].t / 1_000), liveCandles[index].c);
            }
          });
        }
        if (stopChartUserCrosshair !== null) {
          const index = tideCursorMinute === null
            ? null
            : candleForMinute(liveCandles.map((bar) => bar.t), rungSpanMs(liveRung), tideCursorMinute);
          if (index === null) clearChartTimeCursor(primarySection);
          else setChartTimeCursor(primarySection, Math.floor(liveCandles[index].t / 1_000), liveCandles[index].c);
        }
      })
      : null;
    const storedCurvePrefs = storedVwapCurvePrefs(chartId, undefined, [slot]);
    curvePrefs = storedCurvePrefs.prefs;
    curvePrefsError = storedCurvePrefs.error;
    const storedSet = storedLevelSet(chartId);
    levelSet = storedSet.set;
    levelSetError = storedSet.error;
    const setCurveColors = (tokens: ReturnType<typeof resolveTokens>) => {
      curveColors = {
        "--vwap": tokens["--vwap"],
        "--magnet": tokens["--magnet"],
        "--up": tokens["--up"],
        "--flip": tokens["--flip"],
      };
    };
    if (primarySection) setCurveColors(resolveTokens(primarySection));
    const stopCurveTheme = primarySection
      ? watchTheme(primarySection, setCurveColors)
      : null;
    const stopPaneCensus = primarySection
      ? observeMountedPaneCount(primarySection, (count) => {
        linkedPaneMarker = `LINKED ${count}-PANE`;
      })
      : null;
    void loadLevels();
    // The dealer's own staleness budget (3m), never the price leg's 90s.
    const levelsTimer = setInterval(() => void loadLevels(), 180_000);
    let disposed = false;
    let unsubscribeVwap = () => {};
    if (chartRole === "lower") {
      void loadWalls();
      let lastWallPollAt = Date.now();
      wallTimer = setInterval(() => {
        const now = Date.now();
        if (!pollDue(now, lastWallPollAt, cashVenueClosed?.verdict)) return;
        lastWallPollAt = now;
        void loadWalls();
      }, 5_000);
    }
    void (async () => {
      candlePollInFlight = true;
      try { await load(); }
      finally { candlePollInFlight = false; }
      await tick();
      await markFirstPaint(chartId);
      if (disposed) return;
      firstPaintReady = true;
      void ensureVisibleCurveCoverage();
      unsubscribeVwap = startVwapPolling((snapshot) => {
        vwapFeed = snapshot.feed;
        vwapError = snapshot.error;
        vwapLoaded = true;
      });
      await eagerFillHistory();
    })();
    const loadCurrent = load;
    let lastCandlePollAt = Date.now();
    {
    const load = () => {
      const now = Date.now();
      if (!pollDue(now, lastCandlePollAt, cashVenueClosed?.verdict)) return;
      if (candlePollInFlight) { pollsSkipped += 1; return; }
      lastCandlePollAt = now;
      candlePollInFlight = true;
      void loadCurrent().finally(() => { candlePollInFlight = false; });
    };
    const candleTimer = setInterval(() => void load(), pollIntervalMs());
    void probeSubmin();
    // ⛔ A TIMER WITHOUT A TEARDOWN IS A LEAK, and this panel is mounted TWICE (one per chart).
    return () => {
      disposed = true;
      clearInterval(levelsTimer);
      unsubscribeVwap();
      if (wallTimer !== null) clearInterval(wallTimer);
      clearInterval(candleTimer);
      stopPaneCensus?.();
      stopCurveTheme?.();
      stopTideCursor();
      stopChartCursorReady?.();
      stopChartUserCrosshair?.();
      stopChartUserCrosshair = null;
      chartCursorHost = null;
    };
    }
  });
  // R1/R3: retain a named malformed alert instead of dropping the map.
  let refusedRows = $derived.by(() => {
    if (levels0dte === null) return { state: "NO_BODY" as const };
    try { return readRefusedRows(levels0dte); }
    catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error(`[PrimaryChartPanel] /api/dealer/levels ${message}`);
      return { state: "MALFORMED" as const, message };
    }
  });
</script>
<script module lang="ts">
  const firstPaintDone = new Set<string>();
  let releaseFirstPaint: () => void = () => {};
  const bothFirstPainted = new Promise<void>((resolve) => { releaseFirstPaint = resolve; });
  function markFirstPaint(chartId: string) {
    firstPaintDone.add(chartId);
    if (firstPaintDone.has("primaryA") && firstPaintDone.has("primaryB")) releaseFirstPaint();
    return bothFirstPainted;
  }
</script>


<section
  bind:this={primarySection}
  class="card primary"
  role="group"
  data-chart-id={chartId}
  aria-label={`${slot || "ES1!"} linked chart`}
  data-chart-root={slot || "ES1!"}
  data-chart-info-open={String(infoOpen)}
  data-vwap-curve-study="VWAP CURVES · HYPOTHESIS"
  data-vwap-curve-more={curveSet.more}
  data-vwap-more={vwapMore}
  data-wall-more={wallMore}
  data-cvd-bars={cvd?.bars.length ?? 0}
  data-price-bars={candles.length}
  data-rung={rungLabel(liveRung)}
  data-title-loading={titleRung.loading}
  data-served-state={servedState}
  data-feed-error={err ?? ""}
  data-cvd-error={cvdErr ?? ""}
  data-price-error={priceErr ?? ""}
  data-polls-skipped={pollsSkipped}
  data-feed-alert={String(Boolean(err || priceErr || cvdErr || historyError || curveCoverageError || vwapError || (wallError && !cashVenueClosed)))}
  data-stale-alert={String(chartFreshnessStates.includes("stale"))}
  data-cvd-gap={String((cvd?.bars.length ?? 0) === 0)}
  data-price-gap={String(candles.length === 0)}
  class:focus-fit={focusHeight !== null}
  data-live-t={liveLast?.t ?? undefined}
  data-live-close={liveLast?.c ?? undefined}
  data-live-forming={liveLast === null ? undefined : liveIsForming ? "1" : "0"}
  data-live-tick-ms={tickMs ?? undefined}
  data-live-rung={rungKey(liveRung)}
  onpointerdowncapture={armHistoryGesture}
  onpointerupcapture={finishAnchorGesture}
  onwheelcapture={armHistoryGesture}>
  <!-- CLOSED-EVIDENCE-1d (HUDCHAT-31 1ec47665, B constrained): a door failure is ink in the chart frame's alert
       lane, never in the header. A header that fits one row has no room for a chip, so a header alert grows it.
       One snippet, two mounts: the frame's absolute lane when the frame renders, a static line when it does not. -->
  {#snippet headerAlerts()}
    {#if refusedRows.state === "REFUSED"}<span class="chip warn chart-lane-alert" data-refused-rows role="status">REFUSED {refusedRows.total} {refusedRows.total === 1 ? "ROW" : "ROWS"}</span>{/if}
    {#if refusedRows.state === "MALFORMED"}<span class="chip warn chart-lane-alert" data-refused-rows role="alert">{refusedRows.message}</span>{/if}
    {#if levels0dteError}
      <span class="chip warn chart-lane-alert" data-chart-alert role="alert" title={levels0dteError}>LEVELS ERROR · DOOR FAILURE</span>
    {/if}
    {#if curvePrefsError !== null}
      <span class="chip warn chart-lane-alert" data-chart-alert role="alert" title={curvePrefsError}>VWAP PREFS ERROR · STORAGE FAILURE</span>
    {/if}
    {#if curveCoverageError !== null}
      <span class="chip warn chart-lane-alert" data-chart-alert role="alert" title={curveCoverageError}>VWAP CURVE ERROR · COVERAGE FAILURE</span>
    {/if}
  {/snippet}
  <header>
    <h2>{slot}ES1! · {titleRung.text} · {linkedPaneMarker}</h2>
    <RungPills
      options={offeredRungs}
      current={liveRung}
      label={`timeframe for ${slot || "ES1!"}`}
      onchange={(next: Rung) => {
        // ⛔ Minute rungs keep the legacy bare wire form; only sub-minute needs a namespaced key.
        // ASSIGN AND RELOAD: assign-only waits for a poll this panel does not own and makes the
        // control decorative at the exact moment the owner asks it to move.
        historyEpoch += 1;
        historyLoading = false;
        historyGestureArmed = false;
        rung = next.kind === "submin" ? rungKey(next) : next.tf;
        void (async () => {
          await load(next);
          await eagerFillHistory(next);
        })();
      }} />
    <StalenessChip
      label={dataFeedLabel}
      pollKey={dataPollSeq}
      {ageS}
      {budgetS}
      error={dataFeedFault.error}
      lastBarMs={candles.at(-1)?.t ?? null}
      barSpanMs={rungSpanMs(liveRung)}
      compactLive />
    <AgePill payload={levels0dte} fetchedAtMs={levelsFetchedAtMs} door={levelsRequestKey ?? "/api/dealer/levels"} />
    <StalenessChip
      label="VWAP"
      pollKey={vwapFeed}
      ageS={vwapFeed?.age_s ?? null}
      budgetS={vwapFeed?.budget_s ?? 90}
      error={vwapError}
      barSpanMs={60_000}
      servedVenueState={cashVenueClosed?.verdict ?? null}
      compactLive
    />
    <div class="vwap-curve-controls" role="group" aria-label={`${slot || "ES1!"} VWAP curves`}>
      <span class="age hypothesis" data-hyp>HYP</span>
      {#each [
        ["session", "VWAP"], ["avwap", "aVWAP"], ["D", "D"], ["W", "W"], ["M", "M"],
      ] as item}
        <button
          class:active={curvePrefs[item[0] as VwapCurveToggle]}
          data-vwap-toggle={item[1]}
          aria-pressed={curvePrefs[item[0] as VwapCurveToggle]}
          onclick={() => toggleCurve(item[0] as VwapCurveToggle)}>{item[1]}</button>
      {/each}
      {#each pulledAnchors as anchor (anchor)}
        <button
          class="remove-anchor"
          data-remove-avwap={anchor}
          aria-label={`remove anchored VWAP ${new Date(anchor).toISOString()}`}
          onclick={() => removePulledAnchor(anchor)}>× {new Date(anchor).toISOString().slice(11, 16)}Z</button>
      {/each}
    </div>
    <div class="level-set-controls" role="group" aria-label={`${slot || "ES1!"} level set`} data-level-set-pill>
      {#each LEVEL_SET_NAMES as name (name)}
        <button
          class:active={levelSet === name}
          data-level-set={name}
          aria-pressed={levelSet === name}
          onclick={() => chooseLevelSet(name)}>{name}{#if levelSet === name && levelSetNote !== null}{` · ${levelSetNote}`}{/if}</button>
      {/each}
    </div>
    <!-- ORDER 44 — the pan-left fetch is a WAIT, not a stall. A history page is a database read
         over a year of minutes and the chart edge is motionless while it runs; without this the
         owner cannot tell "fetching" from "there is nothing older", and those are opposite
         answers. It is present ONLY while a request is in flight, so its ABSENCE is also a
         statement. -->
    {#if historyLoading}
      <span class="chip loading" data-history-chip aria-live="polite">LOADING HISTORY · depth {historyDepth}</span>
    {/if}
    {#if cashVenueClosed}
      <VenueClosedBanner venueState={cashVenueClosed} />
    {:else if chartRole === "lower"}
      <!-- HUD-CHARTB-WALLS-CHIP-1: walls load only on the lower chart (loadWalls), so only it mounts their chip. -->
      {#if wallAwaitingBasis}
        <span class="wall-awaiting" title="all wall alerts are awaiting a strictly prior ES basis bar">WALLS · awaiting basis</span>
      {:else}
        {#if wallAgeVerdict !== null && wallError === null}
          <span class:stale={wallAgeVerdict === "STALE"} class:closed={wallAgeVerdict === "CLOSED"}
            class="age same-door-verdict" data-raw-verdict={wallAgeVerdict.toLowerCase()}>WALLS {wallAgeVerdict}</span>
        {:else}
          <StalenessChip
            label="WALLS"
            pollKey={wallFeed}
            ageS={wallFeed?.age_s ?? null}
            budgetS={wallFeed?.budget_s ?? 180}
            error={wallError}
            barSpanMs={0}
            servedVenueState={wallFeed?.venue_state?.verdict ?? null}
            compactLive
          />
        {/if}
        <AgePill payload={wallFeed} fetchedAtMs={wallFetchedAtMs} door={wallRequestKey ?? "/api/es/wall-alerts?limit=50"} root={wallFeed?.root ?? "SPX"} />
      {/if}
    {/if}
    <button
      class="chart-info-toggle"
      data-chart-info-toggle
      aria-label={`${slot || "ES1!"} chart information`}
      aria-controls={`${chartInfoId} ${studyInfoId}`}
      aria-expanded={infoOpen}
      onclick={() => (infoOpen = !infoOpen)}>ⓘ</button>
    <div id={chartInfoId} class="chart-info-panel" data-chart-info-panel hidden={!infoOpen}>
      <span class="chip hyp">VWAP CURVES · HYPOTHESIS</span>
      <span class="note" data-ghost-legend>Δ10 · a ghost's signed 10-bar net CVD at its appearance bar</span>
      {#if contractNote}<span class="note" data-contract-note>{contractNote}</span>{/if}
      {#if basisAsOf}<span class="note" data-basis-asof>{basisAsOf}</span>{/if}
      {#if levels0dteError}<span class="note" data-chart-alert-detail="levels">LEVELS ERROR · {levels0dteError}</span>{/if}
      {#if refusedRows.state === "REFUSED"}
        <span class="note">/api/dealer/levels</span>
        {#each refusedRows.items as item}<span class="note">{item.field}:{item.reason} · {item.count} dropped before the levels were drawn</span>{/each}
      {:else if refusedRows.state === "UNSERVED"}
        <span class="note">refused rows · UNSERVED (worker before 8b72948f)</span>
      {:else if refusedRows.state === "MALFORMED"}
        <span class="note">/api/dealer/levels · {refusedRows.message}</span>
      {/if}
      <span class="note" data-level-set-info>LEVEL SET · {levelSet}</span>
      {#each levelSetNotes as note (note)}<span class="note" data-level-set-value>{note}</span>{/each}
      {#each levelSetView.absent as clause (clause)}<span class="note" data-level-set-absent>{clause}</span>{/each}
      {#if levelSetError !== null}<span class="note" data-chart-alert-detail="level-set">LEVEL SET PREFS ERROR · {levelSetError}</span>{/if}
      {#if curvePrefsError !== null}<span class="note" data-chart-alert-detail="vwap-prefs">VWAP PREFS ERROR · {curvePrefsError}</span>{/if}
      {#if curveCoverageError !== null}<span class="note" data-chart-alert-detail="vwap-curve">VWAP CURVE ERROR · {curveCoverageError}</span>{/if}
      {#if wallRefusalCount > 0}
        <span class="note" data-wall-refusals>WALLS · {wallRefusalCount} alert{wallRefusalCount === 1 ? "" : "s"} awaiting basis</span>
      {/if}
      <!-- The success claim remains armed through root data attributes while its prose is opt-in. -->
      {#if loaded && !err}
        <span class="note">
          {cvd?.bars.length ?? 0} cvd bars · {candles.length} price bars · rung {rungLabel(liveRung)} —
          {servedState === "SERVED" ? "served, not fixtured" : "PARTIAL — see the gap notices"}
        </span>
      {/if}
    </div>
  </header>

  {#if (err || !loaded) && (levels0dteError || curvePrefsError !== null || curveCoverageError !== null || refusedRows.state === "REFUSED" || refusedRows.state === "MALFORMED")}
    <div class="chart-alert-lane static" data-chart-alert-lane="static">{@render headerAlerts()}</div>
  {/if}
  {#if rungBad}
    <p class="err" data-chart-alert><b>RUNG ERROR</b> — unknown rung key {rungBad}; refusing a silent 1m substitution</p>
  {/if}
  {#if err}
    <p class="err" data-chart-alert><b>FEED ERROR</b> — {err}</p>
  {:else if !loaded}
    <span class="loading-dot" aria-label="chart loading" title="chart loading"></span>
  {:else}
    {#if cvdErr}
      <p class="err" data-chart-alert><b>CVD FEED ERROR</b> — {cvdErr}</p>
    {/if}
    {#if priceErr}
      <p class="err" data-chart-alert><b>PRICE FEED ERROR</b> — {priceErr}</p>
    {/if}
    <!--
      R771④ / GK-am's COND: the HOST names the chart, with the identity it actually FETCHED —
      root AND this chart's rung — and the embedded vehicle emits nothing. One marker per chart,
      and it lands on `<h2>`, the surface am's guards already read.
    -->
    <div class:show-info={infoOpen} class="chart-frame">
      {#if levels0dteError || curvePrefsError !== null || curveCoverageError !== null || refusedRows.state === "REFUSED" || refusedRows.state === "MALFORMED" || curveSet.incomplete.length > 0}
        <div class="chart-alert-lane" data-chart-alert-lane="frame">
          {@render headerAlerts()}
          {#if curveSet.incomplete.length > 0}
            <span class="vwap-curve-incomplete chip warn chart-lane-alert" data-chart-alert>VWAP CURVE INCOMPLETE · {curveSet.incomplete.join(" · ")}</span>
          {/if}
        </div>
      {/if}
      <SessionCvdPane
        {cvd} cvdFeed={cvdFeed} cvdBarSpanMs={rungSpanMs(liveRung)} cvdBlocked={cvdErr}
        {price} candles={liveCandles} declaredResidue={declaredResidue} {height} label="" standaloneNote={false}
        {focusHeight}
        focusRungKey={loadedCandleRung as RungKey | null}
        {stackMarker}
        levels={renderLevels}
        showLevels={true}
        {chartRole}
        timeframe={rung}
        {rangeFeed}
        rangeTicks={rangeTicks}
        onRangeTicksChange={(next: 8 | 12) => void loadRange(next)}
        {activeWindow}
        bind:showInfo={infoOpen}
        infoPanelId={studyInfoId}
        onVisibleWindowChange={handleVisibleWindowChange}
        displayMode={cvdMode}
        onDisplayModeChange={onCvdModeChange}
      />
      <div
        class="vwap-curve-overlay"
        aria-hidden="true"
        data-vwap-incomplete={JSON.stringify(curveSet.incomplete)}
        data-vwap-curve-readback={JSON.stringify(curveReadback)}>
        {#each curveSet.drawn as curve (curve.id)}
          <span data-series-id={curve.id} data-vwap-token={vwapCurveToken(curve.id)}></span>
        {/each}
      </div>
      {#if curveSet.more > 0}<span class="vwap-curve-more">+{curveSet.more} more</span>{/if}
      <div
        class:stale={vwapFeed?.stale === true}
        class="vwap-overlay"
        style={`top:${vwapOverlayTop}px;height:${vwapOverlayHeight}px`}
        data-vwap-readback={JSON.stringify(vwapReadback)}>
        {#each drawnVwapLevels as item (item.id)}
          <div
            class={`vwap-line ${item.dash} ${item.tokenClass}`}
            data-series-id={item.id}
            data-vwap-token={item.token}
            style={`top:${Math.max(0, Math.min(100, (vwapRange.high - item.level.value) / Math.max(0.01, vwapRange.high - vwapRange.low) * 100))}%`}>
            <span>{item.label}</span>
          </div>
        {/each}
        {#if vwapMore > 0}<span class="vwap-more">+{vwapMore} more</span>{/if}
      </div>
      <div
        class:stale={wallFeed?.stale === true}
        class="wall-alert-overlay"
        data-wall-readback={JSON.stringify(wallReadback)}>
        {#each wallLevels as level (wallId(level.wall_strike_spx))}
          {@const event = wallEventForStrike(level.wall_strike_spx)}
          <div
            class:alerted={event !== undefined}
            class:level-2={event?.level === 2}
            class="wall-level-line"
            data-wall-level-id={wallId(level.wall_strike_spx)}
            style={`top:${wallPosition(level.wall_es)}%`}>
            <span>γ {level.wall_gamma >= 0 ? "+" : ""}{(level.wall_gamma / 1000).toFixed(1)}k · SPX {level.wall_strike_spx}</span>
          </div>
        {/each}
        {#each drawnWallEvents as event, index (wallId(event.wall_strike_spx))}
          <div
            class={`wall-chip level-${event.level}`}
            data-wall-chip-id={wallId(event.wall_strike_spx)}
            data-series-id={wallId(event.wall_strike_spx)}
            style={`top:${Math.min(94, 4 + index * 10)}%`}>
            {wallLabel(event)}
          </div>
        {/each}
        {#if wallMore > 0}<span class="wall-more">+{wallMore} more</span>{/if}
      </div>
    </div>
  {/if}
</section>

<style>
  .primary { display: flex; flex-direction: column; min-width: 0; }
  .primary:not(.focus-fit) { flex: 1 1 auto; }
  /* HUD-390-CLOSED-HEADER-1: focusHeight is the HOST's target (the cell minus this chrome); the section fills
     its cell and the host takes the remainder by flex. Pinning the section to focusHeight took the chrome
     twice and collapsed the chart at 390 CLOSED. */
  .primary.focus-fit { height: 100%; min-height: 0; overflow: hidden; }
  /* Tokens only — law 8. `card` is mounted on the queried section itself, and these are the same
     chrome primitives every existing cockpit card uses; component scoping therefore cannot turn
     the gate-visible class into unstyled markup. */
  .card {
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow);
  }
  header { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; margin: 0 0 6px; }
  .chart-alert-lane { display: flex; align-items: center; min-width: 0; gap: 6px; flex-wrap: nowrap; overflow: hidden; }
  .chart-alert-lane.static { margin: 0 0 6px; }
  .chart-lane-alert {
    display: block; flex: 0 1 auto; min-width: 0; padding: 1px 6px; border: 1px solid var(--stale);
    border-radius: 999px; background: var(--stale-soft); color: var(--stale-ink); font: 10px var(--font-num);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; pointer-events: auto;
  }
  .vwap-curve-controls { display: inline-flex; align-items: center; gap: 3px; }
  .age.hypothesis, .same-door-verdict {
    padding: 1px 6px; border: 1px solid var(--line); border-radius: 999px;
    background: var(--chip); color: var(--ink); font: 700 10px var(--font-num);
  }
  .same-door-verdict.stale, .same-door-verdict.closed {
    border-color: var(--stale); background: var(--stale-soft); color: var(--stale-ink);
  }
  .chart-info-panel { display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .chart-info-panel[hidden] { display: none; }
  .chart-info-toggle {
    width: 20px; height: 20px; padding: 0; border: 1px solid var(--line); border-radius: 999px;
    background: var(--surface); color: var(--muted); cursor: pointer; font: 12px/18px var(--font-num);
  }
  .loading-dot { width: 8px; height: 8px; border-radius: 999px; background: var(--muted); }
  .vwap-curve-controls button {
    border: 1px solid var(--line); border-radius: 4px; padding: 2px 5px;
    background: var(--chip); color: var(--muted); font: 10px var(--font-num); cursor: pointer;
  }
  .vwap-curve-controls button.active { border-color: var(--vwap); color: var(--ink); }
  .vwap-curve-controls .remove-anchor { border-style: dashed; }
  /* HUB-LEVEL-SET-1 L2: the LEVEL SET pill. The text pairs are the VWAP buttons' graded ones; the active
     border is the levels' own --magnet, so the pill reads as belonging to the lines it picks. */
  .level-set-controls { display: inline-flex; align-items: center; gap: 0; }
  .level-set-controls button {
    border: 1px solid var(--line); padding: 2px 5px;
    background: var(--chip); color: var(--muted); font: 10px var(--font-num); cursor: pointer;
  }
  .level-set-controls button:first-child { border-radius: 4px 0 0 4px; }
  .level-set-controls button:last-child { border-radius: 0 4px 4px 0; border-left-width: 0; }
  .level-set-controls button.active { border-color: var(--magnet); color: var(--ink); }
  .level-set-controls button.active:last-child { border-left-width: 1px; margin-left: -1px; }
  /* ORDER 44 — the pan-left wait chip. `--muted` on `--chip` is the pair this file already ships
     on `.vwap-curve-controls button` and the one `validate-palette` has already graded; a new
     colour for a transient status would be a palette claim nobody measured. */
  .chip.loading {
    padding: 1px 6px; border-radius: 999px;
    background: var(--chip); color: var(--muted); font: 10px var(--font-num);
  }
  .chart-frame { position: relative; min-width: 0; }
  .primary:not(.focus-fit) > .chart-frame { margin-top: auto; }
  .focus-fit .chart-frame { min-height: 0; flex: 1 1 auto; overflow: hidden; }
  .vwap-curve-overlay {
    display: none;
  }
  .vwap-curve-more {
    position: absolute; z-index: 3; right: 4px; top: 52px; padding: 2px 4px;
    background: var(--surface); color: var(--muted); font: 10px var(--font-num);
  }
  .chart-frame > .chart-alert-lane {
    position: absolute; z-index: 4; top: 52px; left: 4px; max-width: calc(100% - 120px);
    pointer-events: none;
  }
  .vwap-overlay { position: absolute; left: 0; right: 0; pointer-events: none; z-index: 3; }
  .vwap-overlay.stale { opacity: .48; }
  .vwap-line { position: absolute; left: 0; right: 0; height: 0; }
  .vwap-line.daily { border-top: 1px solid var(--vwap); color: var(--vwap); }
  .vwap-line.weekly { border-top: 1px solid var(--magnet); color: var(--magnet); }
  .vwap-line.monthly { border-top: 1px solid var(--up); color: var(--up); }
  .vwap-line.dashed { border-top-style: dashed; }
  .vwap-line.dotted { border-top-style: dotted; }
  .vwap-line.double { border-top-style: double; }
  .vwap-line span { position: absolute; right: 4px; top: -9px; padding: 0 3px; background: var(--surface); font: 10px var(--font-num); color: inherit; }
  .vwap-more { position: absolute; right: 4px; bottom: 4px; padding: 2px 4px; background: var(--surface); color: var(--muted); font: 10px var(--font-num); }
  .wall-alert-overlay { position: absolute; left: 0; right: 0; top: 48px; height: 61%; pointer-events: none; z-index: 4; }
  .wall-alert-overlay.stale { color: var(--muted); }
  .wall-level-line { position: absolute; left: 0; right: 0; border-top: 1px solid var(--magnet); color: var(--magnet); height: 0; }
  .wall-level-line.level-2 { border-top-style: dashed; }
  .wall-level-line span { position: absolute; right: 4px; top: -9px; padding: 0 3px; background: var(--surface); font: 10px var(--font-num); }
  .chart-frame:not(.show-info) .vwap-curve-more,
  .chart-frame:not(.show-info) .vwap-line span,
  .chart-frame:not(.show-info) .vwap-more,
  .chart-frame:not(.show-info) .wall-level-line span,
  .chart-frame:not(.show-info) .wall-more { display: none; }
  /* ⛔ TEXT IS --ink, NOT --magnet, AND THE BORDER CARRIES THE IDENTITY. `--magnet` on `--surface`
     measures 3.41:1 in the LIGHT theme against a 4.5 AA floor — a NEW failure category that entered
     main with 22-S2 (GK-bg 6325e2fe). The chip was encoding "magnet" twice, in border AND text, and
     only the border has to. ***DISTINGUISH BY STRUCTURE, NOT COLOUR*** — 26-S2's own binding clause,
     applied to the chip that broke it. The file already agreed with itself one line down: :746 gives
     the STALE chip `--muted` (5.91:1, PASS), so the LESS important state was the accessible one. */
  .wall-chip { position: absolute; right: 4px; min-width: 74px; padding: 2px 5px; color: var(--ink); background: var(--surface); border: 1px solid var(--magnet); font: 10px var(--font-num); text-align: right; }
  .wall-chip.level-1 { border-radius: 5px; }
  .wall-chip.level-2 { border-radius: 0; clip-path: polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%); }
  .wall-alert-overlay.stale .wall-level-line,
  .wall-alert-overlay.stale .wall-chip { color: var(--muted); border-color: var(--muted); }
  .wall-more { position: absolute; right: 4px; bottom: 4px; padding: 2px 4px; color: var(--muted); background: var(--surface); font: 10px var(--font-num); }
  .wall-awaiting {
    margin-left: 0; padding: 2px 7px; border: 1px solid var(--line); border-radius: 5px;
    color: var(--muted); background: var(--surface-2); font: 600 10px var(--font-num);
    letter-spacing: 0.06em; white-space: nowrap;
  }
  .wall-awaiting::before { content: "◦ "; color: var(--faint); }
  h2 { font-size: 13px; color: var(--ink); margin: 0; font-weight: 600; }
  .note { font-size: 11px; color: var(--faint); }
  .err { font-size: 12px; color: var(--stale-ink); margin: 0 0 6px; }
</style>
